import React, { useMemo, useState } from "react";
import {
  AppleOutlined,
  MoonOutlined,
  SunOutlined,
  WindowsOutlined,
} from "@ant-design/icons";
import { Button, Popover, Tooltip, theme as antTheme } from "antd";
import { useTheme } from "../theme/ThemeContext";
import { getClientOsFamily, THEME_MODES } from "../theme/themeConstants";

function SystemThemeIcon() {
  const Icon = useMemo(
    () => (getClientOsFamily() === "apple" ? AppleOutlined : WindowsOutlined),
    [],
  );
  return <Icon />;
}

function modeIcon(mode) {
  if (mode === THEME_MODES.LIGHT) return <SunOutlined />;
  if (mode === THEME_MODES.DARK) return <MoonOutlined />;
  return <SystemThemeIcon />;
}

function modeLabel(mode) {
  if (mode === THEME_MODES.LIGHT) return "Light";
  if (mode === THEME_MODES.DARK) return "Dark";
  return "System";
}

export default function ThemePicker({ variant = "floating" }) {
  const { mode, setMode } = useTheme();
  const { token } = antTheme.useToken();
  const [open, setOpen] = useState(false);

  const options = useMemo(
    () => [
      { mode: THEME_MODES.SYSTEM, label: "System", icon: <SystemThemeIcon /> },
      { mode: THEME_MODES.LIGHT, label: "Light", icon: <SunOutlined /> },
      { mode: THEME_MODES.DARK, label: "Dark", icon: <MoonOutlined /> },
    ],
    [],
  );

  const content = (
    <div
      role="listbox"
      aria-label="Theme"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 140,
      }}
    >
      {options.map(({ mode: optionMode, label, icon }) => {
        const active = mode === optionMode;
        return (
          <button
            key={optionMode}
            type="button"
            role="option"
            aria-selected={active}
            onClick={() => {
              setMode(optionMode);
              setOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 12px",
              border: "none",
              borderRadius: token.borderRadius,
              cursor: "pointer",
              textAlign: "left",
              font: "inherit",
              background: active ? token.colorPrimaryBg : "transparent",
              color: active ? token.colorPrimary : token.colorText,
            }}
          >
            {icon}
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );

  const isHeader = variant === "header";
  const tooltipTitle = `Theme: ${modeLabel(mode)}`;

  const trigger = (
    <Button
      type={isHeader ? "text" : "default"}
      shape={isHeader ? "default" : "circle"}
      size={isHeader ? "middle" : "large"}
      className={isHeader ? "app-header-icon-btn" : undefined}
      icon={modeIcon(mode)}
      aria-label={`Theme: ${mode}. Click to change.`}
      style={
        isHeader
          ? undefined
          : {
              background: token.colorBgContainer,
              color: token.colorText,
              border: `1px solid ${token.colorBorderSecondary}`,
              boxShadow: token.boxShadowSecondary,
            }
      }
    />
  );

  const popoverTrigger = isHeader ? (
    <Tooltip title={tooltipTitle} placement="bottom">
      {trigger}
    </Tooltip>
  ) : (
    trigger
  );

  const picker = (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement={isHeader ? "bottomRight" : "topRight"}
      overlayInnerStyle={{ padding: 8 }}
    >
      {popoverTrigger}
    </Popover>
  );

  if (isHeader) {
    return picker;
  }

  return (
    <div
      className="theme-picker-floating"
      style={{
        position: "fixed",
        right: 24,
        bottom: 24,
        zIndex: 1000,
      }}
    >
      {picker}
    </div>
  );
}
